import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import { useRoute } from '@react-navigation/native';
import { useIsFocused } from '@react-navigation/native';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import DocumentView from '../../components/documentViewer';
import FilePicker from '../../components/filePicker/filepicker.web';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { EmailValidation } from './validation';
import { AntDesign, MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { SHADOWS, LAYOUT } from '../../constants/theme';
import { MenuItem, Select } from '@material-ui/core';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* ─── Yes / No toggle ──────────────────────────────────────────── */
const YesNo = ({ value, onChange }) => (
  <View style={yn.row}>
    <TouchableOpacity
      style={[yn.btn, value === 'Yes' && yn.btnActive]}
      onPress={() => onChange('Yes')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'Yes' && yn.txtActive]}>Yes</Text>
    </TouchableOpacity>
    <TouchableOpacity
      style={[yn.btn, value === 'No' && yn.btnActive]}
      onPress={() => onChange('No')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'No' && yn.txtActive]}>No</Text>
    </TouchableOpacity>
  </View>
);
const yn = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8 },
  btn: { paddingHorizontal: 24, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, borderColor: '#D1D5DB', backgroundColor: '#fff' },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  txt: { fontSize: 14, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});

/* ─── Upload Drop Zone ──────────────────────────────────────────── */
const UploadZone = ({ onDataSuccess }) => (
  <View style={uz.wrap}>
    <View style={uz.iconWrap}>
      <Ionicons name="cloud-upload-outline" size={22} color="#6C2BD9" />
    </View>
    <FilePicker style={{ width: '100%' }} onDataSuccess={onDataSuccess} />
    <Text style={uz.hint}>
      Drag &amp; drop files here or <Text style={uz.link}>click to select</Text>
    </Text>
  </View>
);
const uz = StyleSheet.create({
  wrap: { borderWidth: 1.5, borderStyle: 'dashed', borderColor: '#c4b5fd', borderRadius: 10, paddingVertical: 22, paddingHorizontal: 12, alignItems: 'center', backgroundColor: '#faf7ff' },
  iconWrap: { width: 38, height: 38, borderRadius: 19, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  hint: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },
  link: { color: '#6C2BD9', fontWeight: '600' },
});

/* ─── Form field (styled TextInput) ─────────────────────────────── */
const FormField = ({ label, placeholder, value, onChangeText, ...rest }) => (
  <View style={ff.wrap}>
    <Text style={ff.label}>{label}</Text>
    <TextInputComponent
      title={''}
      onChangeText={onChangeText}
      value={value}
      {...rest}
    />
  </View>
);
const ff = StyleSheet.create({
  wrap: { flex: 1, minWidth: 140 },
  label: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 4 },
});

/* ─── Section Card ───────────────────────────────────────────────── */
const SectionCard = ({ isMobile, iconName, iconColor, iconBg, title, children }) => (
  <View style={[sc.card, isMobile && sc.cardMobile]}>
    <View style={sc.headerRow}>
      <View style={[sc.iconWrap, { backgroundColor: iconBg }]}>
        <MaterialCommunityIcons name={iconName} size={18} color={iconColor} />
      </View>
      <Text style={sc.title}>{title}</Text>
    </View>
    {children}
  </View>
);
const sc = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 14, padding: 20, marginBottom: 16, ...SHADOWS.sm },
  cardMobile: { padding: 14 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 },
  iconWrap: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 15, fontWeight: '700', color: '#111827' },
});



/* ─── Insurance Provider Dropdown ────────────────────────────────── */
const InsuranceDropdown = ({ value, onChange }) => (
  <View style={dp.wrap}>
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', height: 42, backgroundColor: '#fff', borderRadius: 8 }}
      variant="outlined"
      displayEmpty
    >
      <MenuItem value={''}>Select provider</MenuItem>
      <MenuItem value={'Building Only'}>Building Only</MenuItem>
      <MenuItem value={'Contents Only'}>Contents Only</MenuItem>
      <MenuItem value={'Building & Contents'}>Building &amp; Contents</MenuItem>
      <MenuItem value={'Other'}>Other</MenuItem>
    </Select>
  </View>
);
const dp = StyleSheet.create({
  wrap: { marginBottom: 12 },
});

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const financeDetails = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const focused = useIsFocused();
  const activePropertyId = routeParams.id;
  const propertyDisplayName = routeParams.name || 'Property';
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  /* ── State (unchanged from original) ── */
  const [state, setState] = useState({
    insurance_type: '',
    insurance_expiry_date: '',
    insurance_premium_amount: '',
    insurance_company_name: '',
    insurance_policy_number: '',
    insurance_company_website: '',
    insurance_company_telephone: '',
    insurance_company_email: '',
    lender_name: '',
    mortgage_account_number: '',
    lender_website: '',
    lender_contact_number: '',
    lender_email: '',
    mortgage_monthly_amount: '',
    mortgage_discount_rate: '',
    mortgage_discount_rate_ends: '',
    mortgage_variable_rate: '',
    mortgage_variable_rate_ends: '',
    mortgage_outstanding_amount: '',
    id: '',
    file_name: '',
    property_insurance_documents: [],
    lender_info_documents: [],
    mortgage_finance_documents: [],
  });

  const [emailError, setEmailError] = useState('');
  const [toggleForInsurance, setToggleForInsurance] = useState();
  const [toggleForLender, setToggleForLender] = useState();
  const [toggleForMortgage, setToggleForMortgage] = useState();

  /* ── API calls (unchanged) ── */
  const _handleChange = (e) => setState({ ...state, [e.label]: e?.value });

  const _getPropertyFinanceDetails = (id) => {
    if (!id) return;
    ajax.get(Urls.Finance + '/' + id)
      .then(({ data }) => {
        if (data.status) {
          setState({
            ...state,
            lender_name: data?.payload?.lender_name,
            mortgage_account_number: data?.payload?.mortgage_account_number,
            lender_website: data?.payload?.lender_website,
            lender_contact_number: data?.payload?.lender_contact_number,
            lender_email: data?.payload?.lender_email,
            mortgage_monthly_amount: data?.payload?.mortgage_monthly_amount,
            mortgage_outstanding_amount: data?.payload?.mortgage_outstanding_amount,
            mortgage_discount_rate: data?.payload?.mortgage_discount_rate,
            mortgage_variable_rate: data?.payload?.mortgage_variable_rate,
            mortgage_variable_rate_ends: data?.payload?.mortgage_variable_rate_ends,
            mortgage_discount_rate_ends: data?.payload?.mortgage_discount_rate_ends,
            insurance_type: data?.payload?.insurance_type,
            insurance_expiry_date: data?.payload?.insurance_expiry_date,
            insurance_premium_amount: data?.payload?.insurance_premium_amount,
            insurance_company_name: data?.payload?.insurance_company_name,
            insurance_policy_number: data?.payload?.insurance_policy_number,
            insurance_company_website: data?.payload?.insurance_company_website,
            insurance_company_telephone: data?.payload?.insurance_company_telephone,
            insurance_company_email: data?.payload?.insurance_company_email,
            property_insurance_documents: data?.payload?.property_insurance_documents,
            lender_info_documents: data?.payload?.lender_info_documents,
            mortgage_finance_documents: data?.payload?.mortgage_finance_documents,
          });
          setToggleForInsurance(data.payload?.is_property_insurance_available ? 'Yes' : 'No');
          setToggleForLender(data.payload?.is_mortgage_info_available ? 'Yes' : 'No');
          setToggleForMortgage(data.payload?.is_mortgage_finance_available ? 'Yes' : 'No');
        }
      })
      .catch((error) => console.log('error', error));
  };

  useEffect(() => {
    if (focused && activePropertyId) _getPropertyFinanceDetails(activePropertyId);
  }, [focused, activePropertyId]);

  const _validate = () => {
    if (state?.lender_email) {
      let EmailValidate = EmailValidation(state?.lender_email);
      if (EmailValidate.status) {
        _onSave();
      } else {
        setEmailError(EmailValidate?.status ? '' : EmailValidate.msg);
      }
    } else {
      _onSave();
    }
  };

  const _onSave = () => {
    const requestBody = {
      lender_name: state?.lender_name || null,
      mortgage_account_number: state?.mortgage_account_number || null,
      lender_website: state?.lender_website || null,
      lender_contact_number: state?.lender_contact_number || null,
      lender_email: state?.lender_email || null,
      mortgage_monthly_amount: state?.mortgage_monthly_amount || null,
      mortgage_outstanding_amount: state?.mortgage_outstanding_amount || null,
      mortgage_discount_rate: state?.mortgage_discount_rate || null,
      mortgage_variable_rate: state?.mortgage_variable_rate || null,
      mortgage_variable_rate_ends: state?.mortgage_variable_rate_ends || null,
      mortgage_discount_rate_ends: state?.mortgage_discount_rate_ends || null,
      insurance_type: state?.insurance_type || null,
      insurance_expiry_date: state?.insurance_expiry_date || null,
      insurance_premium_amount: state?.insurance_premium_amount || null,
      insurance_company_name: state?.insurance_company_name || null,
      insurance_policy_number: state?.insurance_policy_number || null,
      insurance_company_website: state?.insurance_company_website || null,
      insurance_company_telephone: state?.insurance_company_telephone || null,
      insurance_company_email: state.insurance_company_email || null,
      is_property_insurance_available: toggleForInsurance && toggleForInsurance === 'Yes' ? true : false,
      is_mortgage_info_available: toggleForLender && toggleForLender === 'Yes' ? true : false,
      is_mortgage_finance_available: toggleForMortgage && toggleForMortgage === 'Yes' ? true : false,
      file_name: state?.file_name || null,
      property_insurance_documents: state?.property_insurance_documents || null,
      lender_info_documents: state?.lender_info_documents || null,
      mortgage_finance_documents: state?.mortgage_finance_documents || null,
    };
    ajax.post(`${Urls.Finance}/${activePropertyId}`, requestBody)
      .then(({ data }) => {
        if (data.status) {
          navigation.navigate('PropertySection', { id: activePropertyId, name: propertyDisplayName });
        } else {
          showErrorToast(data.message);
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _onAddCertifcate = (value, tag) => {
    let obj = { document_url: value[0]?.openbrixName, document_name: value[0]?.name };
    setState({ ...state, [tag]: [...state[tag], obj] });
  };

  const moveToDocs = (data) => {
    ajax.post(Urls.MoveToDocs + '/' + activePropertyId, data)
      .then(({ data }) => {
        if (data.status) showSuccesToast('Document moved to myDocs section!');
        else showErrorToast('Something went wrong!');
      })
      .catch(() => showErrorToast('Something went wrong!'));
  };

  const _onRemove = (obj, tag, i, ismoveToDocs) => {
    let arr = state[tag];
    if (ismoveToDocs) {
      arr.splice(i, 1);
      setState({ ...state, [tag]: arr });
    } else {
      arr[i].is_deleted = true;
      setState({ ...state, [tag]: arr });
    }
  };

  /* ── Document renderer ── */
  const renderDocs = (docArray, tag) =>
    docArray
      ?.filter((e) => !e?.is_deleted)
      .map((e, i) => (
        <DocumentView
          key={i}
          title={e?.document_name}
          url={e?.document_url}
          directDelete
          onDelete={(move) => _onRemove(e, tag, i, move)}
        />
      ));

  /* ════════════════════════════
     MOBILE LAYOUT
  ════════════════════════════ */
  if (isMobile) {
    return (
      <View style={ms.root}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={ms.scroll}
          showsVerticalScrollIndicator={false}
        >

          {/* Header card */}
          <View style={ms.headerCard}>
            <View style={ms.headerIcon}>
              <MaterialCommunityIcons name="bank-outline" size={20} color="#6C2BD9" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ms.headerTitle}>Finance</Text>
              <Text style={ms.headerSub}>Please add the financial information relating to this property.</Text>
            </View>
          </View>

          {/* Mark as Complete */}
          <View style={ms.completeCard}>
            <MarkAsComplete
              label={'Insurance Information'}
              flowID={route?.params?.id}
              flowtype={route?.params?.key}
              isComplete={route?.params?.status === 2}
            />
          </View>

          {/* Property Insurance */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionTitle}>Add Property Insurance Info?</Text>
            <YesNo value={toggleForInsurance} onChange={setToggleForInsurance} />

            {toggleForInsurance === 'Yes' && (
              <View style={{ marginTop: 16 }}>
                <TouchableOpacity
                  style={ms.quoteBtn}
                  onPress={() => navigation.navigate('financeInsuranceQuote')}
                  activeOpacity={0.85}
                >
                  <Text style={ms.quoteBtnText}>Get Quote</Text>
                </TouchableOpacity>

                <Text style={ms.fieldLabel}>Insurance Provider</Text>
                <InsuranceDropdown
                  value={state.insurance_type}
                  onChange={(val) => _handleChange({ value: val, label: 'insurance_type' })}
                />

                <Text style={ms.fieldLabel}>Equity Rate</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'insurance_premium_amount' })}
                  value={state.insurance_premium_amount}
                  isAmount
                />

                <Text style={ms.fieldLabel}>Company Name</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_name' })}
                  value={state.insurance_company_name}
                />

                <Text style={ms.fieldLabel}>Policy Number</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'insurance_policy_number' })}
                  value={state.insurance_policy_number}
                />

                <Text style={ms.fieldLabel}>Company Website</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_website' })}
                  value={state.insurance_company_website}
                />

                <Text style={ms.fieldLabel}>Telephone</Text>
                <TextInputComponent
                  isNumeric
                  onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_telephone' })}
                  value={state.insurance_company_telephone}
                />

                <Text style={ms.fieldLabel}>Email</Text>
                <TextInputComponent
                  error={emailError}
                  onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_email' })}
                  value={state.insurance_company_email}
                />

                <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Upload Documents</Text>
                <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'property_insurance_documents')} />
                {renderDocs(state.property_insurance_documents, 'property_insurance_documents')}
              </View>
            )}
          </View>

          {/* Add Occupancy Start Date */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionTitle}>Add Lender Info?</Text>
            <YesNo value={toggleForLender} onChange={setToggleForLender} />

            {toggleForLender === 'Yes' && (
              <View style={{ marginTop: 16 }}>
                <Text style={ms.fieldLabel}>Lender Name</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'lender_name' })}
                  value={state.lender_name}
                />
                <Text style={ms.fieldLabel}>Mortgage Account Number</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_account_number' })}
                  value={state.mortgage_account_number}
                />
                <Text style={ms.fieldLabel}>Lender Website</Text>
                <TextInputComponent
                  onChangeText={(e) => _handleChange({ value: e, label: 'lender_website' })}
                  value={state.lender_website}
                />
                <Text style={ms.fieldLabel}>Lender Contact Number</Text>
                <TextInputComponent
                  isNumeric
                  onChangeText={(e) => _handleChange({ value: e, label: 'lender_contact_number' })}
                  value={state.lender_contact_number}
                />
                <Text style={ms.fieldLabel}>Lender Email</Text>
                <TextInputComponent
                  error={emailError}
                  onChangeText={(e) => _handleChange({ value: e, label: 'lender_email' })}
                  value={state.lender_email}
                />
                <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Upload Documents</Text>
                <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'lender_info_documents')} />
                {renderDocs(state.lender_info_documents, 'lender_info_documents')}
              </View>
            )}
          </View>

          {/* Add Mortgage Finance Info */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionTitle}>Add Mortgage Finance Info?</Text>
            <YesNo value={toggleForMortgage} onChange={setToggleForMortgage} />

            {toggleForMortgage === 'Yes' && (
              <View style={{ marginTop: 16 }}>
                <Text style={ms.fieldLabel}>Monthly Amount</Text>
                <TextInputComponent
                  isAmount
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_monthly_amount' })}
                  value={state.mortgage_monthly_amount}
                />
                <Text style={ms.fieldLabel}>Discount Rate % (Mortgage)</Text>
                <TextInputComponent
                  allowDecimal
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_discount_rate' })}
                  value={state.mortgage_discount_rate}
                />
                <Text style={ms.fieldLabel}>Discount Rate Ends</Text>
                <TextInputComponent
                  isDate
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_discount_rate_ends' })}
                  date={state?.mortgage_discount_rate_ends}
                />
                <Text style={ms.fieldLabel}>Variable Rate % (Mortgage)</Text>
                <TextInputComponent
                  allowDecimal
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_variable_rate' })}
                  value={state.mortgage_variable_rate}
                />
                <Text style={ms.fieldLabel}>Variable Rate Ends</Text>
                <TextInputComponent
                  isDate
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_variable_rate_ends' })}
                  date={state?.mortgage_variable_rate_ends}
                />
                <Text style={ms.fieldLabel}>Outstanding Mortgage Amount</Text>
                <TextInputComponent
                  isAmount
                  onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_outstanding_amount' })}
                  value={state?.mortgage_outstanding_amount}
                />
                <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Upload Documents</Text>
                <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'mortgage_finance_documents')} />
                {renderDocs(state.mortgage_finance_documents, 'mortgage_finance_documents')}
              </View>
            )}
          </View>

          <View style={{ height: 100 }} />
        </ScrollView>

        <PropertyStickyFooter onSave={_validate} isMobile={isMobile} />
      </View>
    );
  }

  /* ════════════════════════════
     WEB / TABLET LAYOUT
  ════════════════════════════ */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F7FB' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={activePropertyId}
          propertyName={propertyDisplayName}
          currentStep="Finance & Insurance"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="card-outline"
          title="Finance & Insurance"
          subtitle="Please add the finance and insurance details for this property."
          isMobile={isMobile}
          rightComponent={
            activePropertyId ? (
              <MarkAsComplete
                label={'Finance & Insurance'}
                flowID={activePropertyId}
                flowtype={routeParams?.key}
                isComplete={routeParams?.status === 2}
              />
            ) : null
          }
        />

        {/* Body */}
        <View style={ws.body}>
          {/* Main card */}
          <View style={ws.mainCard}>
            {/* ── Insurance Section ── */}
            <View style={ws.infoRow}>
              <Text style={ws.infoLabel}>Add Property Insurance Info?</Text>
              <YesNo value={toggleForInsurance} onChange={setToggleForInsurance} />
            </View>

            {toggleForInsurance === 'Yes' && (
              <View style={ws.expandedSection}>
                <TouchableOpacity
                  style={ws.quoteBtn}
                  onPress={() => navigation.navigate('financeInsuranceQuote')}
                  activeOpacity={0.85}
                >
                  <Text style={ws.quoteBtnText}>Get Quote</Text>
                </TouchableOpacity>

                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Insurance Provider</Text>
                    <InsuranceDropdown
                      value={state.insurance_type}
                      onChange={(val) => _handleChange({ value: val, label: 'insurance_type' })}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Equity Rate</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'insurance_premium_amount' })}
                      value={state.insurance_premium_amount}
                      isAmount
                    />
                  </View>
                </View>

                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Company Name</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_name' })}
                      value={state.insurance_company_name}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Policy Number</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'insurance_policy_number' })}
                      value={state.insurance_policy_number}
                    />
                  </View>
                </View>

                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Company Website</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_website' })}
                      value={state.insurance_company_website}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Telephone</Text>
                    <TextInputComponent
                      isNumeric
                      onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_telephone' })}
                      value={state.insurance_company_telephone}
                    />
                  </View>
                </View>

                <View style={ws.formGrid}>
                  <View style={[ws.formCol, { flex: 2 }]}>
                    <Text style={ws.fieldLabel}>Email</Text>
                    <TextInputComponent
                      error={emailError}
                      onChangeText={(e) => _handleChange({ value: e, label: 'insurance_company_email' })}
                      value={state.insurance_company_email}
                    />
                  </View>
                </View>

                <Text style={ws.fieldLabel}>Upload Documents</Text>
                <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'property_insurance_documents')} />
                {renderDocs(state.property_insurance_documents, 'property_insurance_documents')}
              </View>
            )}

            <View style={ws.divider} />

            {/* ── Lender Info Section ── */}
            <View style={ws.infoRow}>
              <Text style={ws.infoLabel}>Add Lender Info?</Text>
              <YesNo value={toggleForLender} onChange={setToggleForLender} />
            </View>

            {toggleForLender === 'Yes' && (
              <View style={ws.expandedSection}>
                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Lender Name</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'lender_name' })}
                      value={state.lender_name}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Mortgage Account Number</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_account_number' })}
                      value={state.mortgage_account_number}
                    />
                  </View>
                </View>
                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Lender Website</Text>
                    <TextInputComponent
                      onChangeText={(e) => _handleChange({ value: e, label: 'lender_website' })}
                      value={state.lender_website}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Lender Contact Number</Text>
                    <TextInputComponent
                      isNumeric
                      onChangeText={(e) => _handleChange({ value: e, label: 'lender_contact_number' })}
                      value={state.lender_contact_number}
                    />
                  </View>
                </View>
                <View style={ws.formGrid}>
                  <View style={[ws.formCol, { flex: 2 }]}>
                    <Text style={ws.fieldLabel}>Lender Email</Text>
                    <TextInputComponent
                      error={emailError}
                      onChangeText={(e) => _handleChange({ value: e, label: 'lender_email' })}
                      value={state.lender_email}
                    />
                  </View>
                </View>
                <Text style={ws.fieldLabel}>Upload Documents</Text>
                <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'lender_info_documents')} />
                {renderDocs(state.lender_info_documents, 'lender_info_documents')}
              </View>
            )}

            <View style={ws.divider} />

            {/* ── Mortgage Finance Section ── */}
            <View style={ws.infoRow}>
              <Text style={ws.infoLabel}>Add Mortgage Finance Info?</Text>
              <YesNo value={toggleForMortgage} onChange={setToggleForMortgage} />
            </View>

            {toggleForMortgage === 'Yes' && (
              <View style={ws.expandedSection}>
                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Monthly Amount</Text>
                    <TextInputComponent
                      isAmount
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_monthly_amount' })}
                      value={state.mortgage_monthly_amount}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Discount Rate % (Mortgage)</Text>
                    <TextInputComponent
                      allowDecimal
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_discount_rate' })}
                      value={state.mortgage_discount_rate}
                    />
                  </View>
                </View>
                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Discount Rate Ends</Text>
                    <TextInputComponent
                      isDate
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_discount_rate_ends' })}
                      date={state?.mortgage_discount_rate_ends}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Variable Rate % (Mortgage)</Text>
                    <TextInputComponent
                      allowDecimal
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_variable_rate' })}
                      value={state.mortgage_variable_rate}
                    />
                  </View>
                </View>
                <View style={ws.formGrid}>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Variable Rate Ends</Text>
                    <TextInputComponent
                      isDate
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_variable_rate_ends' })}
                      date={state?.mortgage_variable_rate_ends}
                    />
                  </View>
                  <View style={ws.formCol}>
                    <Text style={ws.fieldLabel}>Outstanding Mortgage Amount</Text>
                    <TextInputComponent
                      isAmount
                      onChangeText={(e) => _handleChange({ value: e, label: 'mortgage_outstanding_amount' })}
                      value={state?.mortgage_outstanding_amount}
                    />
                  </View>
                </View>
                <Text style={ws.fieldLabel}>Upload Documents</Text>
                <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'mortgage_finance_documents')} />
                {renderDocs(state.mortgage_finance_documents, 'mortgage_finance_documents')}
              </View>
            )}
          </View>

          {/* Sidebar */}
          <SetupSidebar
            activeKey="finance"
            propertyId={activePropertyId}
            propertyName={propertyDisplayName}
            navigation={navigation}
          />
        </View>
      </ScrollView>

      {/* Sticky footer */}
      <PropertyStickyFooter onSave={_validate} isMobile={isMobile} />
    </View>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */

/* ── MOBILE ── */
const ms = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  scroll: { padding: 16, paddingBottom: 30 },

  progressCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 12, ...SHADOWS.sm },
  progressRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  progressTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  progressStep: { fontSize: 12, color: '#6C2BD9', fontWeight: '700' },
  track: { height: 6, backgroundColor: '#F0E8FF', borderRadius: 3, overflow: 'hidden' },
  fill: { width: '87%', height: '100%', backgroundColor: '#6C2BD9', borderRadius: 3 },

  headerCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, ...SHADOWS.sm },
  headerIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
  headerSub: { fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 17 },

  completeCard: { backgroundColor: '#fff', borderRadius: 14, paddingHorizontal: 16, paddingVertical: 4, marginBottom: 12, ...SHADOWS.sm },

  sectionCard: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, ...SHADOWS.sm },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: '#111827', marginBottom: 12 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 4 },
  quoteBtn: { backgroundColor: '#6C2BD9', borderRadius: 8, paddingHorizontal: 20, paddingVertical: 10, alignSelf: 'flex-start', marginBottom: 16 },
  quoteBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  footer: { backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, ...SHADOWS.md },
  footerNote: { fontSize: 12, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 20, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

/* ── WEB / TABLET ── */
const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },

  breadcrumbBar: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 24, paddingVertical: 14, flexWrap: 'wrap' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  /* Banner Card */
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},

  body: { flexDirection: 'row', alignItems: 'flex-start', gap: 20, paddingHorizontal: 24, paddingBottom: 100 },
  mainCard: { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 24, ...SHADOWS.md },

  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', flexWrap: 'wrap' },
  cardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#111827' },
  cardSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  infoRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, flexWrap: 'wrap', gap: 12 },
  infoLabel: { fontSize: 14, fontWeight: '700', color: '#111827', flex: 1 },
  expandedSection: { paddingTop: 4, paddingBottom: 20 },
  divider: { height: 1, backgroundColor: '#F3F4F6', marginVertical: 4 },

  quoteBtn: { backgroundColor: '#6C2BD9', borderRadius: 8, paddingHorizontal: 20, paddingVertical: 10, alignSelf: 'flex-start', marginBottom: 20 },
  quoteBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  formGrid: { flexDirection: 'row', gap: 16, marginBottom: 12, flexWrap: 'wrap' },
  formCol: { flex: 1, minWidth: 180 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },

  stickyFooter: { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 24, paddingVertical: 14, zIndex: 1000, ...SHADOWS.md },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 24, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

export default drawerHoc({
  Component: financeDetails,
  showBackButton: true,
  noScroll: true,
});
